import { isMobilePreferences, type MobilePreferences } from "./preferences.js";
import type { RecentSearch, SavedTravelItem } from "./explore-state.js";

export interface AccountExploreState {
  readonly recentSearches: readonly RecentSearch[];
  readonly savedItems: readonly SavedTravelItem[];
}

export function mergeRecentSearches(local: readonly RecentSearch[], cloud: readonly RecentSearch[]): readonly RecentSearch[] {
  const newest = new Map<string, RecentSearch>();
  for (const item of [...cloud, ...local]) {
    if (!item.query.trim() || !item.category.trim() || !Number.isFinite(Date.parse(item.searchedAt))) continue;
    const key = `${item.category}\u0000${item.query.trim().toLocaleLowerCase()}`;
    const existing = newest.get(key);
    if (existing === undefined || Date.parse(item.searchedAt) > Date.parse(existing.searchedAt)) newest.set(key, Object.freeze({ ...item, query: item.query.trim() }));
  }
  return Object.freeze([...newest.values()]
    .sort((left, right) => Date.parse(right.searchedAt) - Date.parse(left.searchedAt) || left.category.localeCompare(right.category) || left.query.localeCompare(right.query))
    .slice(0, 10));
}

export function mergeSavedItems(local: readonly SavedTravelItem[], cloud: readonly SavedTravelItem[]): readonly SavedTravelItem[] {
  const newest = new Map<string, SavedTravelItem>();
  for (const item of [...cloud, ...local]) {
    if (!item.targetId.trim() || !Number.isFinite(Date.parse(item.savedAt))) continue;
    const key = `${item.kind}\u0000${item.targetId}`;
    const existing = newest.get(key);
    if (existing === undefined || Date.parse(item.savedAt) > Date.parse(existing.savedAt)) newest.set(key, Object.freeze({ ...item }));
  }
  return Object.freeze([...newest.values()]
    .sort((left, right) => Date.parse(right.savedAt) - Date.parse(left.savedAt) || left.kind.localeCompare(right.kind) || left.targetId.localeCompare(right.targetId))
    .slice(0, 500));
}

export function mergeAccountExploreState(local: AccountExploreState, cloud: AccountExploreState): AccountExploreState {
  return Object.freeze({
    recentSearches: mergeRecentSearches(local.recentSearches, cloud.recentSearches),
    savedItems: mergeSavedItems(local.savedItems, cloud.savedItems)
  });
}

export interface CloudPreferencesEnvelope {
  readonly preferences: MobilePreferences | null;
  readonly updatedAt: string | null;
}

export function chooseAccountPreferences(local: MobilePreferences, cloud: CloudPreferencesEnvelope): MobilePreferences {
  return isMobilePreferences(cloud.preferences) ? cloud.preferences : local;
}
